import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { Meeting, Transcript, Summary } from "@/types";

interface UseMeetingReturn {
  meeting: Meeting | null;
  transcript: Transcript | null;
  summary: Summary | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMeeting(meetingId: string | undefined): UseMeetingReturn {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!meetingId) return;

    const [meetingRes, transcriptRes, summaryRes] = await Promise.all([
      supabase.from("meetings").select("*").eq("id", meetingId).single(),
      supabase
        .from("transcripts")
        .select("*")
        .eq("meeting_id", meetingId)
        .maybeSingle(),
      supabase
        .from("summaries")
        .select("*")
        .eq("meeting_id", meetingId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (meetingRes.error) {
      setError(meetingRes.error.message);
      setLoading(false);
      return;
    }

    setMeeting(meetingRes.data);
    setTranscript(transcriptRes.data);
    setSummary(summaryRes.data);
    setLoading(false);
  }, [meetingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll while processing
  useEffect(() => {
    if (
      !meeting ||
      meeting.status === "done" ||
      meeting.status === "error"
    )
      return;

    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [meeting?.status, fetchData]);

  return { meeting, transcript, summary, loading, error, refresh: fetchData };
}
